import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Calculator, Plus, Trash2, RefreshCw, FileDown, Save, Settings } from 'lucide-react';
import { estimateAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import * as XLSX from 'xlsx';

const Estimate = () => {
  const { canAdd, canEdit, canDelete } = useAuth();
  const [estimates, setEstimates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingRows, setEditingRows] = useState(new Set());
  const [showDefaultsDialog, setShowDefaultsDialog] = useState(false);
  const [defaults, setDefaults] = useState([]);
  const [defaultsLoading, setDefaultsLoading] = useState(false);

  const itemOptions = ['Pendant', 'Ring', 'Stud', 'Nosepin', 'Bangle', 'Necklace', 'Bracelet'];
  const shapeOptions = ['Round', 'Rectangle', 'Square', 'Pear', 'Triangle', 'Alphabet', 'Cushion', 'Emerald', 'Mix Shape', 'Marquise/boat', 'Baguette'];

  // Load estimates
  const loadEstimates = async () => {
    try {
      setIsLoading(true);
      const data = await estimateAPI.getAll();
      setEstimates(data);
    } catch (error) {
      toast.error('Failed to load estimates');
      console.error('Error loading estimates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadEstimates();
    loadDefaults();
  }, []);

  // Load defaults
  const loadDefaults = async () => {
    try {
      const data = await estimateAPI.getDefaults();
      setDefaults(data);
    } catch (error) {
      console.error('Error loading defaults:', error);
    }
  };

  // Get default Cts for size+shape combination
  const getDefaultCts = (size, shape) => {
    // Convert size to string for exact comparison
    const sizeStr = String(size);
    const defaultEntry = defaults.find(d => String(d.size_mm) === sizeStr && d.shape === shape);
    return defaultEntry ? defaultEntry.default_cts : '';
  };

  // Calculate auto-computed values
  const calculateRow = (estimate) => {
    const pcs = parseFloat(estimate.pcs) || 0;
    const cts = parseFloat(estimate.cts) || 0;
    const pricePerCarat = parseFloat(estimate.price_per_carat) || 0;

    const totalCts = pcs * cts;
    const cost = totalCts * pricePerCarat;

    return {
      ...estimate,
      total_cts: totalCts.toFixed(3),
      cost: cost.toFixed(2)
    };
  };

  // Add new empty row
  const handleAddRow = () => {
    const newRow = {
      id: `temp_${Date.now()}`,
      item: '',
      shape: '',
      size_mm: '',
      cts: '',
      pcs: '',
      price_per_carat: '',
      total_cts: '0.000',
      cost: '0.00',
      isNew: true
    };
    setEstimates([...estimates, newRow]);
    setEditingRows(new Set([...editingRows, newRow.id]));
  };

  // Update estimate value
  const handleCellChange = (id, field, value) => {
    setEstimates(estimates.map(est => {
      if (est.id === id) {
        const updated = { ...est, [field]: value };
        return calculateRow(updated);
      }
      return est;
    }));
  };

  // Auto-populate Cts when size input loses focus
  const handleSizeBlur = (id) => {
    setEstimates(estimates.map(est => {
      if (est.id === id) {
        const updated = { ...est };
        
        // Only auto-populate if both size and shape are set
        if (updated.size_mm && updated.shape) {
          // Convert to string for exact comparison
          const sizeStr = String(updated.size_mm);
          const defaultCts = getDefaultCts(sizeStr, updated.shape);
          
          if (defaultCts) {
            updated.cts = defaultCts;
          }
        }
        
        return calculateRow(updated);
      }
      return est;
    }));
  };

  // Auto-populate Cts when shape is selected
  const handleShapeChange = (id, shape) => {
    setEstimates(estimates.map(est => {
      if (est.id === id) {
        const updated = { ...est, shape };
        
        // Auto-populate if size is already entered
        if (updated.size_mm && shape) {
          const sizeStr = String(updated.size_mm);
          const defaultCts = getDefaultCts(sizeStr, shape);
          
          if (defaultCts) {
            updated.cts = defaultCts;
          }
        }
        
        return calculateRow(updated);
      }
      return est;
    }));
  };

  // Save estimate (create or update)
  const handleSaveEstimate = async (estimate) => {
    if (!estimate.item || !estimate.shape || !estimate.size_mm || !estimate.pcs || !estimate.price_per_carat) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      if (estimate.isNew) {
        // Create new estimate
        const { isNew, ...estimateData } = estimate;
        const created = await estimateAPI.create(estimateData);
        setEstimates(estimates.map(e => e.id === estimate.id ? created : e));
        toast.success('Estimate added successfully');
      } else {
        // Update existing estimate
        await estimateAPI.update(estimate.id, estimate);
        toast.success('Estimate updated');
      }
      setEditingRows(new Set([...editingRows].filter(id => id !== estimate.id)));
    } catch (error) {
      toast.error('Failed to save estimate');
      console.error('Error saving estimate:', error);
    }
  };

  // Delete estimate
  const handleDeleteEstimate = async (id, isNew) => {
    if (!window.confirm('Are you sure you want to delete this estimate?')) return;

    if (isNew) {
      // Just remove from local state if it's not saved yet
      setEstimates(estimates.filter(e => e.id !== id));
      setEditingRows(new Set([...editingRows].filter(rowId => rowId !== id)));
      return;
    }

    try {
      await estimateAPI.delete(id);
      setEstimates(estimates.filter(e => e.id !== id));
      toast.success('Estimate deleted');
    } catch (error) {
      toast.error('Failed to delete estimate');
    }
  };

  // Reset all estimates
  const handleResetAll = async () => {
    if (!window.confirm('Are you sure you want to clear all estimates? This action cannot be undone.')) return;

    try {
      await estimateAPI.deleteAll();
      setEstimates([]);
      setEditingRows(new Set());
      toast.success('All estimates cleared');
    } catch (error) {
      toast.error('Failed to reset estimates');
    }
  };

  // Save defaults
  const handleSaveDefaults = async (newDefaults) => {
    try {
      setDefaultsLoading(true);
      await estimateAPI.saveDefaults(newDefaults);
      setDefaults(newDefaults);
      toast.success('Defaults saved successfully');
      setShowDefaultsDialog(false);
    } catch (error) {
      toast.error('Failed to save defaults');
      console.error('Error saving defaults:', error);
    } finally {
      setDefaultsLoading(false);
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    const totals = estimates.reduce((acc, est) => {
      const calculated = calculateRow(est);
      return {
        totalPcs: acc.totalPcs + (parseFloat(est.pcs) || 0),
        totalCts: acc.totalCts + parseFloat(calculated.total_cts || 0),
        totalCost: acc.totalCost + parseFloat(calculated.cost || 0)
      };
    }, { totalPcs: 0, totalCts: 0, totalCost: 0 });

    return totals;
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      const exportData = estimates.map(est => {
        const calculated = calculateRow(est);
        return {
          'Item': est.item,
          'Shape': est.shape,
          'Size (MM)': est.size_mm,
          'Cts': est.cts,
          'Pcs': est.pcs,
          'Total Cts': calculated.total_cts,
          'Price Per Carat': est.price_per_carat,
          'Cost': calculated.cost
        };
      });

      // Add totals row
      const totals = calculateTotals();
      exportData.push({
        'Item': '',
        'Shape': '',
        'Size (MM)': '',
        'Cts': '',
        'Pcs': totals.totalPcs,
        'Total Cts': totals.totalCts.toFixed(3),
        'Price Per Carat': '',
        'Cost': totals.totalCost.toFixed(2)
      });

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Estimates");

      // Auto-size columns
      const colWidths = [
        { wch: 12 }, // Item
        { wch: 15 }, // Shape
        { wch: 12 }, // Size
        { wch: 10 }, // Cts
        { wch: 10 }, // Pcs
        { wch: 12 }, // Total Cts
        { wch: 15 }, // Price Per Carat
        { wch: 12 }, // Cost
      ];
      ws['!cols'] = colWidths;

      // Generate filename
      const today = new Date().toISOString().split('T')[0];
      const filename = `Estimates_${today}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
      toast.success(`Exported ${estimates.length} records to ${filename}`);
    } catch (error) {
      toast.error('Failed to export to Excel');
      console.error('Export error:', error);
    }
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-primary/10">
            <Calculator className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Estimate</h1>
            <p className="text-muted-foreground mt-1">Jewelry cost estimation calculator</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleResetAll}
            disabled={estimates.length === 0}
            className="shadow-sm"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset All
          </Button>
          <Button
            variant="outline"
            onClick={exportToExcel}
            disabled={estimates.length === 0}
            className="shadow-sm"
          >
            <FileDown className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowDefaultsDialog(true)}
            className="shadow-sm"
          >
            <Settings className="h-4 w-4 mr-2" />
            Defaults
          </Button>
          {canAdd('estimate') && (
            <Button className="shadow-emerald" onClick={handleAddRow}>
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
          )}
        </div>
      </div>

      {/* Excel-like Editable Table */}
      <Card>
        <CardHeader>
          <CardTitle>Estimates ({estimates.filter(e => !e.isNew).length})</CardTitle>
          <CardDescription>Click "Add Row" to create new entries. Edit cells directly like Excel.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading estimates...</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <Table className="min-w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[140px]">Item</TableHead>
                      <TableHead className="w-[140px]">Shape</TableHead>
                      <TableHead className="w-[100px]">Size (MM)</TableHead>
                      <TableHead className="w-[100px]">Cts</TableHead>
                      <TableHead className="w-[100px]">Pcs</TableHead>
                      <TableHead className="w-[120px]">Total Cts</TableHead>
                      <TableHead className="w-[120px]">Price/Carat</TableHead>
                      <TableHead className="w-[120px]">Cost</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {estimates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-12">
                          <Calculator className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                          <p className="text-muted-foreground">No estimates yet</p>
                          <p className="text-sm text-muted-foreground mt-2">Click "Add Row" to create your first estimate</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {estimates.map((estimate) => {
                          const calculated = calculateRow(estimate);
                          return (
                            <TableRow key={estimate.id} className={estimate.isNew ? 'bg-blue-50 dark:bg-blue-950/20' : ''}>
                              <TableCell>
                                <Select
                                  value={estimate.item}
                                  onValueChange={(value) => handleCellChange(estimate.id, 'item', value)}
                                >
                                  <SelectTrigger className="h-8 w-full">
                                    <SelectValue placeholder="Select..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {itemOptions.map(item => (
                                      <SelectItem key={item} value={item}>{item}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={estimate.shape}
                                  onValueChange={(value) => handleShapeChange(estimate.id, value)}
                                >
                                  <SelectTrigger className="h-8 w-full">
                                    <SelectValue placeholder="Select..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {shapeOptions.map(shape => (
                                      <SelectItem key={shape} value={shape}>{shape}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={estimate.size_mm}
                                  onChange={(e) => handleCellChange(estimate.id, 'size_mm', e.target.value)}
                                  onBlur={() => handleSizeBlur(estimate.id)}
                                  className="h-8"
                                  placeholder="1.5"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={estimate.cts}
                                  onChange={(e) => handleCellChange(estimate.id, 'cts', e.target.value)}
                                  className="h-8"
                                  placeholder="0.54"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={estimate.pcs}
                                  onChange={(e) => handleCellChange(estimate.id, 'pcs', e.target.value)}
                                  className="h-8"
                                  placeholder="36"
                                />
                              </TableCell>
                              <TableCell className="font-mono text-blue-600 font-semibold">
                                {calculated.total_cts}
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={estimate.price_per_carat}
                                  onChange={(e) => handleCellChange(estimate.id, 'price_per_carat', e.target.value)}
                                  className="h-8"
                                  placeholder="105"
                                />
                              </TableCell>
                              <TableCell className="font-mono text-green-600 font-bold">
                                ₹{calculated.cost}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  {estimate.isNew && (
                                    <Button
                                      size="sm"
                                      variant="default"
                                      onClick={() => handleSaveEstimate(estimate)}
                                      className="h-7 px-2"
                                    >
                                      <Save className="h-3 w-3" />
                                    </Button>
                                  )}
                                  {canDelete('estimate') && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteEstimate(estimate.id, estimate.isNew)}
                                      className="h-7 px-2"
                                    >
                                      <Trash2 className="h-3 w-3 text-destructive" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Totals Row */}
                        <TableRow className="bg-muted/50 font-semibold border-t-2 border-primary/20">
                          <TableCell colSpan={4} className="text-right">TOTAL:</TableCell>
                          <TableCell>{totals.totalPcs}</TableCell>
                          <TableCell className="font-mono text-blue-700">{totals.totalCts.toFixed(3)}</TableCell>
                          <TableCell></TableCell>
                          <TableCell className="font-mono text-green-700 font-bold">₹{totals.totalCost.toFixed(2)}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Defaults Management Dialog */}
      <DefaultsDialog
        open={showDefaultsDialog}
        onOpenChange={setShowDefaultsDialog}
        defaults={defaults}
        shapeOptions={shapeOptions}
        onSave={handleSaveDefaults}
        isLoading={defaultsLoading}
      />
    </div>
  );
};

// Defaults Dialog Component
const DefaultsDialog = ({ open, onOpenChange, defaults, shapeOptions, onSave, isLoading }) => {
  const [localDefaults, setLocalDefaults] = useState([]);

  useEffect(() => {
    if (open) {
      setLocalDefaults(defaults.length > 0 ? [...defaults] : []);
    }
  }, [open, defaults]);

  const handleAddDefault = () => {
    setLocalDefaults([...localDefaults, { size_mm: '', shape: '', default_cts: '' }]);
  };

  const handleUpdateDefault = (index, field, value) => {
    const updated = [...localDefaults];
    updated[index][field] = value;
    setLocalDefaults(updated);
  };

  const handleDeleteDefault = (index) => {
    setLocalDefaults(localDefaults.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    // Validate all entries have size, shape, and cts
    const valid = localDefaults.every(d => d.size_mm && d.shape && d.default_cts);
    if (!valid) {
      toast.error('Please fill all fields for each default entry');
      return;
    }
    onSave(localDefaults);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Default Cts Values
          </DialogTitle>
          <DialogDescription>
            Set default carat values for each Size and Shape combination. These will auto-populate when you add new rows.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex justify-end">
            <Button onClick={handleAddDefault} size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Add Default
            </Button>
          </div>

          {localDefaults.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No defaults set yet</p>
              <p className="text-sm mt-2">Click "Add Default" to create your first default value</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Size (MM)</TableHead>
                    <TableHead>Shape</TableHead>
                    <TableHead>Default Cts</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {localDefaults.map((def, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.1"
                          value={def.size_mm}
                          onChange={(e) => handleUpdateDefault(index, 'size_mm', e.target.value)}
                          placeholder="1.5"
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={def.shape}
                          onValueChange={(value) => handleUpdateDefault(index, 'shape', value)}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="Select shape" />
                          </SelectTrigger>
                          <SelectContent>
                            {shapeOptions.map(shape => (
                              <SelectItem key={shape} value={shape}>{shape}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={def.default_cts}
                          onChange={(e) => handleUpdateDefault(index, 'default_cts', e.target.value)}
                          placeholder="0.54"
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteDefault(index)}
                          className="h-7 px-2"
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-900 dark:text-blue-100">
              <strong>💡 Tip:</strong> When you add a new row and select a Size and Shape combination that has a default, 
              the Cts field will automatically populate with the saved default value.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Defaults'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Estimate;
